import { useEffect, useRef } from "react";

/**
 * Drop-in replacement for `<img>` that defeats WebKit + Chromium
 * mobile's "black tile" bug — where the browser reports the image as
 * `load`-complete but `naturalWidth === 0`, or where an image inside a
 * horizontally-scrolled ancestor is silently skipped during decode.
 *
 * How it works (belt-and-suspenders, no state, no re-renders):
 *   1. `onLoad`: read `naturalWidth`. If zero → treat as failure and
 *      re-request `src` with a cache-buster query param. Up to two
 *      retries; then give up (browser is out of options).
 *   2. Immediately after `onLoad`, call `img.decode()`. If the promise
 *      rejects → same retry path. This forces mobile Chrome to actually
 *      rasterize the bytes it fetched instead of parking them.
 *   3. `onError`: same retry path — covers the traditional 404 /
 *      network case for free.
 *   4. Applies `transform: translateZ(0)` (via `style`) to promote the
 *      element to its own compositor layer. WebKit occasionally skips
 *      the decode-and-paint step for offscreen images in a scroll
 *      container; a compositor layer makes the browser treat the tile
 *      as "always visible" and keeps the bitmap around.
 *
 * All extra props (`className`, `data-testid`, `sizes`, …) pass through
 * to the underlying `<img>`. `style` is merged so callers can layer in
 * additional styles without losing the translateZ hint.
 */
export default function SafeImage({
  src,
  alt = "",
  className = "",
  style = null,
  loading = "eager",
  decoding = "async",
  fetchPriority = "auto",
  onLoad,
  onError,
  ...rest
}) {
  // Retry counter lives on a ref so we don't re-render and cause a
  // fresh <img> mount (which would reset the counter and loop forever
  // on genuinely bad URLs).
  const retriesRef = useRef(0);
  // Remember the base src per element instance so cache-buster query
  // params from a retry don't compound (?r=1&r=2&r=3…).
  const baseSrcRef = useRef(src);

  useEffect(() => {
    // When the caller swaps `src` (e.g. product image changes), reset
    // the retry budget for the new URL.
    baseSrcRef.current = src;
    retriesRef.current = 0;
  }, [src]);

  const scheduleRetry = (img) => {
    const avifBase = baseSrcRef.current || "";
    // AVIF unsupported/undecodable → downgrade once to the WebP variant.
    if (avifBase.endsWith(".avif")) {
      baseSrcRef.current = avifBase.replace(/\.avif$/, ".webp");
      retriesRef.current = 0;
      setTimeout(() => {
        if (img.isConnected) img.src = baseSrcRef.current;
      }, 0);
      return;
    }
    if (retriesRef.current >= 2) return; // give up gracefully
    retriesRef.current += 1;
    const base = baseSrcRef.current || "";
    const sep = base.includes("?") ? "&" : "?";
    // Short delay lets WebKit finish tearing down whatever internal
    // state got wedged. 60ms is empirically enough on iPhone Safari
    // WebKit / mobile Chromium without being visibly slow.
    setTimeout(() => {
      // Guard: element might have unmounted between load and retry.
      if (!img.isConnected) return;
      img.src = `${base}${sep}_sr=${retriesRef.current}`;
    }, 60);
  };

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      decoding={decoding}
      fetchpriority={fetchPriority}
      className={className}
      style={{ transform: "translateZ(0)", ...(style || {}) }}
      onLoad={(e) => {
        const img = e.currentTarget;
        // Detect the "loaded but transparent" WebKit failure.
        if (img.naturalWidth === 0 || img.naturalHeight === 0) {
          scheduleRetry(img);
        } else if (typeof img.decode === "function") {
          // Force decode. If Chrome mobile silently dropped it we get
          // a rejected promise here and can retry.
          img.decode().catch(() => scheduleRetry(img));
        }
        onLoad?.(e);
      }}
      onError={(e) => {
        scheduleRetry(e.currentTarget);
        onError?.(e);
      }}
      {...rest}
    />
  );
}
